import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button, Checkbox, Chip, FormControl, ListItemText, OutlinedInput, SelectChangeEvent} from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {styled} from "@mui/material/styles";
import {useGetRequestSelectOptions} from "../../../hooks/request/options/useGetRequestSelectOptions";
import {optionsEndpoints} from "../../../requests/api-endpoints";

const UserLocationAppInput = styled(FormControl)(() => ({
  width: '100%',
  marginBottom: '1em',
  '& .MuiInputLabel-root': { fontSize: '0.86em' },
}));

interface UserLocationSelectProps {
  selectedLocationIds: string[];
  handleLocationIdsChange: (ids: string[]) => void;
}

/**
 * Component for selecting user locations.
 * Supports multi-select with an "All Locations" toggle.
 */
const UserLocationSelect = ({
  selectedLocationIds,
  handleLocationIdsChange
}: UserLocationSelectProps) => {
  const [open, setOpen] = useState(false);
  const { options } = useGetRequestSelectOptions({ requestUrl: optionsEndpoints.supplier.location });

  useEffect(() => {
    if (!selectedLocationIds?.length) {
      handleLocationIdsChange(['all']);
    }
  }, [handleLocationIdsChange, selectedLocationIds]);

  const onSelectChange = (event: SelectChangeEvent<string[]>) => {
    // A value-less action row (e.g. the "Done" button) or stray interaction can
    // inject `undefined` into the selection, which would serialize to `null` and
    // violate the backend's non-null location constraint. MUI can also hand back a
    // comma-separated string (e.g. browser autofill) even with `multiple`, so
    // normalize to a string[] before dropping any empty entries.
    const rawValue = event.target.value;
    const value = (typeof rawValue === 'string' ? rawValue.split(',') : rawValue).filter(
      (value): value is string => typeof value === 'string' && value.length > 0
    );
    const newSetIncludesAll = value.includes('all');
    const currentSetIncludesAll = selectedLocationIds.includes('all');

    if (!value.length || (!currentSetIncludesAll && newSetIncludesAll)) {
      handleLocationIdsChange(['all']);
    } else if (currentSetIncludesAll && newSetIncludesAll && value.length > 1) {
      handleLocationIdsChange(value.filter(value => value !== 'all'));
    } else {
      handleLocationIdsChange(value);
    }
  };

  const handleClose = useCallback(() => setOpen(false), []);
  const handleOpen = useCallback(() => setOpen(true), []);

  const isAllSelected = selectedLocationIds?.includes('all');

  /**
   * Resolves a location ID to a display label.
   */
  const getLabelForValue = (value: string): string => {
    if (value === 'all') return 'All Locations';
    const option = options.find(option => option.value === value);
    return option?.label || value;
  };

  return <UserLocationAppInput variant='outlined' size="small" required>
    <InputLabel id={"create-edit-user-location-label"}>{"Select Location"}</InputLabel>
    <Select
      labelId={"create-edit-user-location-label"}
      id={"create-edit-user-select-location"}
      value={selectedLocationIds || ['all']}
      onChange={onSelectChange}
      multiple
      input={<OutlinedInput label="Select Location" size="small" />}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      renderValue={(selected) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((value) => (
            <Chip key={value} label={getLabelForValue(value)} size="small" />
          ))}
        </Box>
      )}
      MenuProps={{
        PaperProps: {
          sx: { maxHeight: 300 },
        },
      }}
    >
      <MenuItem value={'all'}>
        <Checkbox checked={isAllSelected} />
        <ListItemText primary="All Locations" />
      </MenuItem>
      {
        options?.length > 0 && options
          .filter(option => typeof option.value === 'string' && option.value.length > 0)
          .map(option => (
            <MenuItem value={option.value} key={option.value}>
              <Checkbox checked={selectedLocationIds?.includes(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))
      }
      <Box
        component="li"
        sx={{
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          p: 1,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Button
          size="small"
          variant="contained"
          color="primary"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClose}
        >
          Done
        </Button>
      </Box>
    </Select>
  </UserLocationAppInput>;
};

export default UserLocationSelect;
